import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import Autocomplete, {
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useDebounce } from "../../utils/hooks/useDebounce";

/** What a picker shows of a code: the code and its name. */
export interface MasterOption {
  code: string;
  name: string;
}

interface MasterPickerProps<Option extends MasterOption> {
  /** The code on the document, which may be one switched off since it was picked. */
  value: MasterOption | null;
  /** Searches the active codes; must keep its identity between renders. */
  search: (text: string) => Promise<Option[]>;
  onChange: (option: Option | null) => void;
  label?: string;
  placeholder: string;
  readOnly?: boolean;
  error?: string;
  minWidth?: number;
}

const SEARCH_DEBOUNCE_MS = 300;

const optionLabel = (option: MasterOption) => `${option.code} ${option.name}`;
const isSameCode = (option: MasterOption, value: MasterOption) => option.code === value.code;
/** The server already matched the code or name; the picker shows its answer as it is. */
const keepServerMatches = (options: MasterOption[]) => options;

/**
 * Picks an Express code (an account or a contact) from the codes head office keeps, searching
 * the code or the name as the keyer types. Only active codes are offered.
 */
export default function MasterPicker<Option extends MasterOption>({
  value,
  search,
  onChange,
  label,
  placeholder,
  readOnly = false,
  error,
  minWidth,
}: Readonly<MasterPickerProps<Option>>) {
  const [open, setOpen] = useState<boolean>(false);
  const [input, setInput] = useState<string>("");
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const query = useDebounce(input.trim(), SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    search(query)
      .then((rows) => {
        if (active) setOptions(rows);
      })
      .catch(() => {
        // The interceptor already shows why the codes could not be read.
        if (active) setOptions([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, query, search]);

  // The picked code stays listed even when the search no longer answers it.
  const shownOptions = useMemo<MasterOption[]>(
    () => (value && !options.some((option) => isSameCode(option, value)) ? [value, ...options] : options),
    [value, options],
  );

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleInputChange = (
    _event: SyntheticEvent,
    text: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    // A pick echoes its label into the box; only the keyer's own typing is a new search.
    if (reason === "input") setInput(text);
  };
  const handleChange = (_event: SyntheticEvent, picked: MasterOption | null) =>
    onChange(picked ? options.find((option) => isSameCode(option, picked)) ?? null : null);

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      size="small"
      label={label}
      placeholder={placeholder}
      error={Boolean(error)}
      helperText={error}
    />
  );

  return (
    <Autocomplete<MasterOption, false, false, false>
      size="small"
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      value={value}
      options={shownOptions}
      loading={loading}
      readOnly={readOnly}
      filterOptions={keepServerMatches}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={isSameCode}
      onInputChange={handleInputChange}
      onChange={handleChange}
      noOptionsText="ไม่พบรหัส"
      loadingText="กำลังค้นหา…"
      sx={{ minWidth }}
      renderInput={renderInput}
    />
  );
}
